import React from 'react';
import { Link } from 'react-router-dom';
import AppFooter from '../components/AppFooter';

// Privacy Policy — modelled on GDPR/DPDP-compliant policies of comparable
// B2B SaaS tools. Covers what we collect, why, retention, sharing, transfers,
// and data-subject rights. Opens in a new tab from the app-wide footer.
export default function PrivacyPolicyPage() {
  const lastUpdated = 'February 1, 2026';
  return (
    <div className="min-h-screen flex flex-col bg-white text-zinc-800" data-testid="privacy-policy-page">
      <header className="border-b border-zinc-100 bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-['Outfit'] text-[17px] font-black tracking-tight" data-testid="privacy-logo">
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white text-[12px] font-black">IW</span>
            Inspiro Projects
          </Link>
          <nav className="flex items-center gap-4 text-[12px] text-zinc-500">
            <Link to="/terms" className="hover:text-zinc-800" data-testid="privacy-to-terms">Terms of Service</Link>
            <a href="mailto:support@inspiroworks.com" className="hover:text-zinc-800">Contact</a>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-indigo-600 mb-2">Legal</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-black tracking-tight text-zinc-900 mb-3">Privacy Policy</h1>
          <p className="text-[13px] text-zinc-500">Last updated: {lastUpdated}</p>
        </div>

        <div className="text-[14px] leading-relaxed space-y-6">
          <p className="text-[15px] text-zinc-700">
            INPW Technologies Pvt Ltd ("Inspiro Projects", "we", "us") respects your privacy. This policy explains what personal data we collect, how we use it, who we share it with, and the choices you have. It applies to our website and the Inspiro Projects Service (the "Service").
          </p>

          <Section num="1" title="Information We Collect">
            <p><strong>Account data</strong> — name, email, profile photo, role, organization you belong to, and authentication metadata (hashed passwords, OAuth tokens from Google / Slack / Microsoft / GitHub).</p>
            <p><strong>Workspace &amp; Customer Data</strong> — projects, tasks, comments, documents, whiteboards, file attachments, custom fields, and related content you or your teammates create.</p>
            <p><strong>Billing data</strong> — we do <em>not</em> store full card numbers. Our payment processor (Razorpay) tokenizes card details. We retain transaction IDs, amount, plan, billing address, and GSTIN where provided.</p>
            <p><strong>Usage &amp; device data</strong> — IP address, browser, OS, device type, referrer, pages viewed, feature events, and error logs. Used to secure the Service and improve performance.</p>
            <p><strong>Support &amp; communications</strong> — messages you send us, support ticket transcripts, NPS responses.</p>
          </Section>

          <Section num="2" title="How We Use Your Data">
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, maintain and secure the Service.</li>
              <li>Authenticate you and enforce workspace-level access controls.</li>
              <li>Process payments and send invoices.</li>
              <li>Send product, security and billing notifications (via SendGrid).</li>
              <li>Detect abuse, fraud, and unauthorized access; comply with legal obligations.</li>
              <li>Measure aggregate product usage to prioritize roadmap (never tied to an identifiable individual outside your workspace).</li>
            </ul>
          </Section>

          <Section num="3" title="Legal Bases (GDPR / DPDP)">
            <p>We process personal data on the following lawful bases: <strong>contract</strong> (to deliver the Service you subscribed to), <strong>legitimate interest</strong> (securing the Service, fighting abuse, product improvement), <strong>consent</strong> (marketing emails, optional cookies), and <strong>legal obligation</strong> (tax records, fraud reporting).</p>
          </Section>

          <Section num="4" title="Sharing and Sub-processors">
            <p>We share data with a limited set of vetted sub-processors who help us run the Service:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>MongoDB Atlas / AWS</strong> — database and infrastructure hosting (ap-south-1 by default).</li>
              <li><strong>Razorpay</strong> — payment processing and invoicing.</li>
              <li><strong>SendGrid</strong> — transactional email delivery.</li>
              <li><strong>Slack, Microsoft, Google, GitHub</strong> — only when you choose to connect those integrations; data exchanged is scoped to the OAuth permissions you grant.</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal data or share it with advertisers.</p>
          </Section>

          <Section num="5" title="Data Retention">
            <p>Customer Data is retained for the lifetime of your workspace. If your workspace is cancelled or deleted, data is kept for <strong>30 days</strong> to support export and recovery requests, then permanently deleted (except where longer retention is required by law, e.g., tax records — 7 years).</p>
          </Section>

          <Section num="6" title="International Transfers">
            <p>Our primary data centre is in India (ap-south-1). Some sub-processors (e.g., SendGrid) may process data outside India. Where this happens we rely on Standard Contractual Clauses or equivalent safeguards. Premium customers may opt for <strong>region-based data storage</strong> (EU, US, or APAC).</p>
          </Section>

          <Section num="7" title="Your Rights">
            <p>Subject to applicable law (GDPR, DPDP Act, CCPA, etc.), you have the right to: <strong>access</strong> your data, <strong>correct</strong> inaccuracies, <strong>export</strong> (portability), <strong>delete</strong> ("right to be forgotten"), <strong>restrict</strong> or <strong>object</strong> to certain processing, and <strong>withdraw consent</strong> at any time.</p>
            <p>To exercise any of these rights, email <a href="mailto:support@inspiroworks.com" className="text-indigo-600 hover:underline">support@inspiroworks.com</a>. Workspace admins can action most requests directly from workspace settings.</p>
          </Section>

          <Section num="8" title="Security">
            <p>We protect Customer Data with: AES-256 encryption at rest, TLS 1.2+ in transit, bcrypt-hashed passwords, tenant isolation at the query layer, signed file-download URLs, brute-force protection, and regular vulnerability scans. No system is perfectly secure; we will notify affected customers of any confirmed breach without undue delay and within 72 hours of discovery, as required by DPDP / GDPR.</p>
          </Section>

          <Section num="9" title="Cookies &amp; Tracking">
            <p>We use strictly-necessary cookies for session authentication (JWT) and preference cookies (theme, workspace). We do not set third-party advertising cookies. Analytics (if any) is limited to aggregated, privacy-preserving event counting — no cross-site profiling.</p>
          </Section>

          <Section num="10" title="Children">
            <p>The Service is not directed at children under 16. We do not knowingly collect data from children. If you believe a child has given us data, contact us and we will delete it.</p>
          </Section>

          <Section num="11" title="Changes to This Policy">
            <p>We may update this Policy to reflect new features or legal requirements. Material changes will be notified in-app or by email at least 14 days in advance.</p>
          </Section>

          <Section num="12" title="Contact &amp; Data Protection Officer">
            <p>For privacy questions or to exercise your rights, write to our DPO at <a href="mailto:support@inspiroworks.com" className="text-indigo-600 hover:underline">support@inspiroworks.com</a> or INPW Technologies Pvt Ltd, Bengaluru, India.</p>
          </Section>
        </div>
      </main>

      <AppFooter variant="public" />
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <section data-testid={`privacy-section-${num}`}>
      <h2 className="font-['Outfit'] text-[20px] font-bold text-zinc-900 mb-2">{num}. {title}</h2>
      <div className="text-zinc-700 space-y-2">{children}</div>
    </section>
  );
}
